import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db, ref, set } from "../Firebase/firebase-config";
import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail } from "firebase/auth";
import { useAccount } from "../context/AccountContext";
import { FiArrowLeft, FiCheckCircle, FiAlertCircle, FiLoader } from "react-icons/fi";
import "./AddFirms.css";

export default function AddFirm() {
  const { currentAccount, reload } = useAccount();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    username: "",
    name: "",
    phone: "",
    email: "",
    gst: "",
    pan: "",
  });

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState(""); // success | error

  // Auto-fill PAN from GST
  const handleGST = (gstValue) => {
    const cleanGST = gstValue.toUpperCase().trim();
    if (cleanGST.length >= 12) {
      const autoPAN = cleanGST.substring(2, 12);
      setForm({ ...form, gst: cleanGST, pan: autoPAN });
    } else {
      setForm({ ...form, gst: cleanGST, pan: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (currentAccount?.type !== "main") {
      setMsg("Only the main account can create firms.");
      setMsgType("error");
      return;
    }

    setMsg("");
    setLoading(true);

    try {
      const cleanPhone = form.phone.replace(/[^\d]/g, "");
      if (cleanPhone.length !== 10) {
        throw new Error("Enter a valid 10-digit phone number");
      }

      const dummyEmail = `${cleanPhone}@firm.altrange.internal`;
      const dummyPwd = Math.random().toString(36).slice(-12) + "Xy1!";

      // Check if phone already used
      const methods = await fetchSignInMethodsForEmail(auth, dummyEmail);
      if (methods.length > 0) {
        throw new Error("This phone number is already linked to a firm.");
      }

      // Create auth user
      const cred = await createUserWithEmailAndPassword(auth, dummyEmail, dummyPwd);
      const firmUid = cred.user.uid;

      // Save firm profile
      await set(ref(db, `profileDashboard/${firmUid}`), {
        type: "firm",
        parentUid: currentAccount.uid,
        username: form.username.trim(),
        company_name: form.name.trim(),
        phone: form.phone,
        email: form.email || null,
        gst: form.gst || null,
        pan: form.pan || null,
        createdAt: new Date().toISOString(),
      });

      // Link firm under main account
      await set(ref(db, `profileDashboard/${currentAccount.uid}/firms/${firmUid}`), {
        uid: firmUid,
        company_name: form.name.trim(),
      });

      setMsg("Firm created successfully!");
      setMsgType("success");
      await reload();
      setTimeout(() => navigate("/dashboard"), 1000);
    } catch (err) {
      console.error(err);
      setMsg(err.message || "Failed to create firm. Try again.");
      setMsgType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-firm-page-wrapper">
      <div className="add-firm-card">
        {/* Header */}
        <div className="add-firm-header">
          <button onClick={() => navigate(-1)} className="back-btn">
            <FiArrowLeft size={28} />
          </button>
          <h1>Add New Firm</h1>
          <div style={{ width: 48 }} />
        </div>

        {/* Alert */}
        <div className="add-firm-card-content">
        {msg && (
          <div className={`alert alert-${msgType}`}>
            {msgType === "success" ? <FiCheckCircle /> : <FiAlertCircle />}
            <span>{msg}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="add-firm-form">
          <div className="form-grid">
            <div className="input-group firm-group">
              <label>Username <span className="required">*</span></label>
              <input
                type="text"
                required
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="e.g. xyz_traders"
              />
            </div>

            <div className="input-group firm-group">
              <label>Firm Name <span className="required">*</span></label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="XYZ Traders Pvt Ltd"
              />
            </div>

            <div className="input-group firm-group">
              <label>Phone Number <span className="required">*</span></label>
              <input
                type="tel"
                required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+91 98765 43210"
                maxLength="15"
              />
            </div>

            <div className="input-group firm-group">
              <label>Email </label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="firm@example.com"
              />
            </div>

            <div className="input-group firm-group">
              <label>GST Number (Optional)</label>
              <input
                type="text"
                value={form.gst}
                onChange={(e) => handleGST(e.target.value)}
                placeholder="22AAAAA0000A1Z5"
                maxLength="15"
                style={{ textTransform: "uppercase" }}
              />
            </div>

            <div className="input-group firm-group">
              <label>PAN (Auto-filled)</label>
              <input
                type="text"
                value={form.pan}
                readOnly
                placeholder="Will be auto-filled from GST"
                className="readonly-input"
              />
            </div>
          </div>

         <div className="form-actions">
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? (
                <>
                  <FiLoader className="spin" />
                  Creating Firm...
                </>
              ) : (
                "Create Firm"
              )}
            </button>
            <button type="button" onClick={() => navigate(-1)} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>

        <div className="info-note">
          <strong>Note:</strong> Firms are sub-accounts linked to your main account. They can manage stock, view reports, and receive notifications — but only you can create or delete them.
        </div>
      </div>
    </div>
  </div>
);
}